import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ticketsApi } from '../services/api';
import { toast } from 'react-hot-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  TicketIcon,
  ExclamationCircleIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

const CreateTicketPage: React.FC = () => {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [category, setCategory] = useState('general');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🔍 === TICKET CREATION CLIENT DEBUG ===');
    console.log('🔍 Form submitted with data:', { subject, description, priority, category });
    
    if (!subject.trim() || !description.trim()) {
      toast.error('Пожалуйста, заполните все обязательные поля');
      return;
    }

    setLoading(true);
    
    try {
      const ticketData = {
        subject: subject.trim(),
        description: description.trim(),
        priority,
        category,
        // Для веб-интерфейса создаем тестовые данные Telegram
        telegramUserId: 'web_user',
        telegramChatId: 'web_chat',
        telegramUsername: 'web_user',
        telegramFirstName: 'Веб',
        telegramLastName: 'Пользователь',
        telegramChatType: 'private'
      };

      console.log('🔍 Sending ticket data to server:', ticketData);
      console.log('🔍 API endpoint:', '/api/tickets');
      
      const response = await ticketsApi.create(ticketData);
      console.log('🔍 Server response:', response);
      
      const ticket = response.data?.data?.ticket || response.data?.ticket;
      console.log('🔍 Extracted ticket:', ticket);
      
      if (ticket) {
        console.log('✅ Ticket created successfully:', ticket.ticketNumber);
        // Показываем уведомление об успешном создании
        toast.success(
          `Заявка #${ticket.ticketNumber} создана успешно!\n\n📝 Описание: ${description}\n\n⏳ Ожидайте ответа оператора. Мы свяжемся с вами в ближайшее время.`,
          { duration: 6000 }
        );
        
        // Перенаправляем на страницу заявок
        setTimeout(() => {
          navigate('/tickets');
        }, 2000);
      } else {
        console.error('❌ No ticket data in response');
        toast.error('Ошибка создания заявки');
      }
    } catch (error: any) {
      console.error('❌ Error creating ticket:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      const message = error.response?.data?.message || 'Ошибка создания заявки';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Заголовок */}
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/tickets')}
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Создание новой заявки
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Заполните форму для создания новой заявки
          </p>
        </div>
      </div>

      {/* Форма создания заявки */}
      <div className="bg-card border border-border shadow rounded-lg">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="text-lg font-medium text-foreground">
            Информация о заявке
          </h3>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Тема заявки */}
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-foreground">
              Тема заявки <span className="text-destructive">*</span>
            </label>
            <Input
              type="text"
              id="subject"
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1"
              placeholder="Кратко опишите проблему"
            />
          </div>

          {/* Описание */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-foreground">
              Подробное описание <span className="text-destructive">*</span>
            </label>
            <textarea
              id="description"
              required
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1 flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Подробно опишите вашу проблему или вопрос"
            />
          </div>

          {/* Приоритет и категория */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="priority" className="block text-sm font-medium text-foreground">
                Приоритет
              </label>
              <select
                id="priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="low">Низкий</option>
                <option value="medium">Средний</option>
                <option value="high">Высокий</option>
                <option value="urgent">Срочный</option>
              </select>
            </div>

            <div>
              <label htmlFor="category" className="block text-sm font-medium text-foreground">
                Категория
              </label>
              <select
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="general">Общие вопросы</option>
                <option value="technical">Техническая поддержка</option>
                <option value="billing">Вопросы оплаты</option>
                <option value="feature">Запрос функций</option>
                <option value="bug">Сообщение об ошибке</option>
              </select>
            </div>
          </div>

          {/* Кнопки */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/tickets')}
            >
              Отмена
            </Button>
            <Button
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Создание...
                </>
              ) : (
                <>
                  <TicketIcon className="h-4 w-4 mr-2" />
                  Создать заявку
                </>
              )}
            </Button>
          </div>
        </form>
      </div>

      {/* Информационный блок */}
      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <ExclamationCircleIcon className="h-5 w-5 text-primary" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-foreground">
              Информация о создании заявки
            </h3>
            <div className="mt-2 text-sm text-muted-foreground">
              <p>После создания заявки вы получите уведомление с номером заявки. Оператор свяжется с вами в ближайшее время для решения вопроса.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTicketPage;
